import { ChevronDown, Ellipsis, Loader2 } from './icons.tsx'
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react'
import { cx } from './cx.ts'

const MENU_WIDTH = 224
const EDGE = 8

/**
 * Where an open menu's left edge goes, relative to its button: right edges lined up, then
 * pulled back inside the screen. On a phone the header's buttons wrap to the left, and a menu
 * hung from the right edge of a button there would open off-screen.
 */
export function menuLeft(
  button: { left: number; right: number },
  viewport: number,
  menuWidth = MENU_WIDTH,
  align: 'start' | 'end' = 'end',
): number {
  const width = Math.min(menuWidth, viewport - 2 * EDGE)
  const wanted = align === 'end' ? button.right - width : button.left
  const onScreen = Math.max(EDGE, Math.min(wanted, viewport - EDGE - width))
  return onScreen - button.left
}

/**
 * Secondary actions behind a "More" button. `collapse="phone"` (the default)
 * shows the buttons in a row from 640px up and folds them into the menu on
 * phones; `"always"` keeps them in the menu, for rare or risky actions that
 * should not sit beside the everyday ones. One set of children either way.
 * The menu is placed when it opens (`menuLeft`), so it never leaves the screen.
 */
export function ActionMenu({
  children,
  label = 'More actions',
  collapse = 'phone',
}: {
  children: ReactNode
  label?: string
  collapse?: 'phone' | 'always'
}) {
  // `left`: the open menu's offset from its button (null while it is closed).
  const [left, setLeft] = useState<number | null>(null)
  const open = left !== null
  const setOpen = (next: boolean) => {
    const rect = box.current?.getBoundingClientRect()
    setLeft(next && rect ? menuLeft(rect, document.documentElement.clientWidth) : null)
  }
  const box = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent) => {
      if (!box.current?.contains(e.target as Node)) setLeft(null)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLeft(null)
    }
    // A turned phone or a resized window moves the button: close rather than float astray.
    const onResize = () => setLeft(null)
    document.addEventListener('pointerdown', onDown)
    document.addEventListener('keydown', onKey)
    window.addEventListener('resize', onResize)
    return () => {
      document.removeEventListener('pointerdown', onDown)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('resize', onResize)
    }
  }, [open])
  const phone = collapse === 'phone'
  return (
    <div ref={box} className={cx('relative', phone && 'sm:contents')}>
      <button
        type="button"
        aria-label={label}
        aria-expanded={open}
        title={label}
        onClick={() => setOpen(!open)}
        className={cx(
          'inline-flex size-9 items-center justify-center rounded-xl border border-border bg-surface text-muted transition-colors hover:bg-soft hover:text-ink',
          phone && 'sm:hidden',
        )}
      >
        <Ellipsis className="size-4" aria-hidden />
      </button>
      {/* A row of buttons on wide screens; the same buttons as menu rows when folded. */}
      <div
        onClick={() => setLeft(null)}
        style={open ? { left } : undefined}
        className={cx(
          'bg-raised',
          phone && 'sm:contents',
          open
            ? 'absolute z-30 mt-1 flex w-56 max-w-[calc(100vw-1rem)] flex-col gap-0.5 rounded-xl border border-border p-1 shadow-card-hover [&>*]:w-full [&>*]:justify-start [&>*]:border-0 [&>*]:bg-transparent'
            : 'hidden',
          phone && 'sm:[&>*]:w-auto sm:[&>*]:justify-center sm:[&>*]:border sm:[&>*]:bg-surface',
        )}
      >
        {children}
      </div>
    </div>
  )
}

const CloseMenu = createContext<(() => void) | null>(null)

/** Rows the arrow keys move between, in order. */
const ROWS = '[role="menuitem"]:not([disabled]),[role="menuitemradio"],[role="radio"]'

/**
 * A button that opens a list of choices: the business switcher, the account
 * menu. Placed with `menuLeft` when it opens (never pinned to an edge), closed
 * by a click outside, Escape, a resize or a chosen item, and driven by the
 * keyboard (arrows, Home/End). Focus moves to the first row on open and back
 * to the button on close. `trigger` is the button's content; `label` is its
 * accessible name.
 */
export function Menu({
  label,
  trigger,
  triggerClassName,
  title,
  width = MENU_WIDTH,
  align = 'end',
  side = 'below',
  fixed = false,
  className,
  children,
}: {
  label: string
  trigger: ReactNode
  triggerClassName?: string
  /** On the wrapper (e.g. `min-w-0 flex-1` to let the button fill a row). */
  className?: string
  title?: string
  width?: number
  align?: 'start' | 'end'
  /**
   * `above` for a button at the bottom of a panel or the screen; `auto` opens above when the
   * button is in the lower part of the screen, so a menu low on a long list stays in view.
   */
  side?: 'below' | 'above' | 'auto'
  /**
   * Float over the page (`position: fixed`) instead of inside the button's box: for a menu on
   * a row of a list whose card clips what overflows it. It closes when the page scrolls.
   */
  fixed?: boolean
  children: ReactNode
}) {
  const [left, setLeft] = useState<number | null>(null)
  const [up, setUp] = useState(side === 'above')
  // With `fixed`: where the panel sits on the screen (left, and its top or bottom edge).
  const [at, setAt] = useState<{ left: number; top?: number; bottom?: number } | null>(null)
  const open = left !== null
  const box = useRef<HTMLDivElement>(null)
  const button = useRef<HTMLButtonElement>(null)
  const panel = useRef<HTMLDivElement>(null)

  const close = (refocus = true) => {
    setLeft(null)
    if (refocus) button.current?.focus()
  }
  const toggle = () => {
    if (open) return close()
    const rect = button.current?.getBoundingClientRect()
    if (!rect) return
    const above = side === 'above' || (side === 'auto' && rect.bottom > window.innerHeight * 0.55)
    const offset = menuLeft(rect, document.documentElement.clientWidth, width, align)
    setUp(above)
    setAt(
      above
        ? { left: rect.left + offset, bottom: window.innerHeight - rect.top + 4 }
        : { left: rect.left + offset, top: rect.bottom + 4 },
    )
    setLeft(offset)
  }

  useEffect(() => {
    if (!open) return
    ;[...(panel.current?.querySelectorAll<HTMLElement>(ROWS) ?? [])]
      .find((r) => r.offsetParent !== null)
      ?.focus({ preventScroll: true })
    const onDown = (e: PointerEvent) => {
      if (!box.current?.contains(e.target as Node)) setLeft(null)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        setLeft(null)
        button.current?.focus()
      }
    }
    const onResize = () => setLeft(null)
    // A fixed panel would stay put while its row scrolls away: close instead.
    const onScroll = (e: Event) => {
      if (!panel.current?.contains(e.target as Node)) setLeft(null)
    }
    document.addEventListener('pointerdown', onDown)
    document.addEventListener('keydown', onKey)
    window.addEventListener('resize', onResize)
    if (fixed) window.addEventListener('scroll', onScroll, true)
    return () => {
      document.removeEventListener('pointerdown', onDown)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('resize', onResize)
      window.removeEventListener('scroll', onScroll, true)
    }
  }, [open, fixed])

  const onPanelKey = (e: ReactKeyboardEvent) => {
    // Rows hidden on this screen size (display: none) can't take focus: skip them.
    const rows = [...(panel.current?.querySelectorAll<HTMLElement>(ROWS) ?? [])].filter(
      (r) => r.offsetParent !== null,
    )
    if (rows.length === 0) return
    const at = rows.indexOf(document.activeElement as HTMLElement)
    let next: number | null = null
    if (e.key === 'ArrowDown') next = (at + 1) % rows.length
    else if (e.key === 'ArrowUp') next = (at - 1 + rows.length) % rows.length
    else if (e.key === 'Home') next = 0
    else if (e.key === 'End') next = rows.length - 1
    if (next === null) return
    e.preventDefault()
    rows[next]?.focus()
  }

  return (
    <div ref={box} className={cx('relative', className)}>
      <button
        ref={button}
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        title={title}
        onClick={toggle}
        className={triggerClassName}
      >
        {trigger}
      </button>
      {open && (
        <div
          ref={panel}
          role="menu"
          aria-label={label}
          onKeyDown={onPanelKey}
          style={{
            ...(fixed && at ? at : { left }),
            width: `min(${width}px, calc(100vw - ${2 * EDGE}px))`,
          }}
          // It grows out of the corner under its button.
          className={cx(
            'z-40 animate-scale-in rounded-xl border border-border bg-raised p-1 shadow-card-hover',
            fixed ? 'fixed' : 'absolute',
            up
              ? cx(
                  !fixed && 'bottom-full mb-1',
                  align === 'end' ? 'origin-bottom-right' : 'origin-bottom-left',
                )
              : cx(!fixed && 'mt-1', align === 'end' ? 'origin-top-right' : 'origin-top-left'),
          )}
        >
          <CloseMenu.Provider value={() => close(false)}>{children}</CloseMenu.Provider>
        </div>
      )}
    </div>
  )
}

/** A row of a `Menu`: runs `onSelect` and closes the menu. */
export function MenuItem({
  onSelect,
  icon,
  hint,
  danger = false,
  disabled = false,
  checked,
  children,
}: {
  onSelect: () => void
  icon?: ReactNode
  /** Small text at the right (a shortcut, "Limit reached"). */
  hint?: ReactNode
  danger?: boolean
  disabled?: boolean
  /** Marks the current choice in a list (the business you are in). */
  checked?: boolean
  children: ReactNode
}) {
  const close = useContext(CloseMenu)
  return (
    <button
      type="button"
      role={checked === undefined ? 'menuitem' : 'menuitemradio'}
      aria-checked={checked}
      disabled={disabled}
      tabIndex={-1}
      onClick={() => {
        close?.()
        onSelect()
      }}
      className={cx(
        'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm outline-none transition-colors',
        'hover:bg-soft focus-visible:bg-soft focus:bg-soft disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent',
        danger ? 'text-danger' : 'text-ink',
      )}
    >
      {icon && <span className="flex shrink-0 items-center text-muted">{icon}</span>}
      <span className="min-w-0 flex-1">{children}</span>
      {hint && <span className="shrink-0 text-xs text-muted">{hint}</span>}
    </button>
  )
}

/** A line between groups of rows, or a small heading over a group. */
export function MenuSeparator() {
  return <div role="separator" className="mx-1 my-1 h-px bg-border" />
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return <div className="px-2.5 pt-2 pb-1 text-section text-subtle">{children}</div>
}

/**
 * One main action with the other ways to do it behind an arrow (Generate PO, and Generate and
 * send). The menu opens above, for a button at the bottom of a panel; its rows are `MenuItem`s.
 */
export function SplitButton({
  label,
  icon,
  onClick,
  disabled = false,
  loading = false,
  menuLabel,
  menuWidth = 320,
  className,
  children,
}: {
  label: ReactNode
  icon?: ReactNode
  onClick: () => void
  disabled?: boolean
  loading?: boolean
  /** The arrow's accessible name and the menu's. */
  menuLabel: string
  menuWidth?: number
  className?: string
  children: ReactNode
}) {
  const fill =
    'inline-flex items-center justify-center bg-primary text-on-fill font-medium transition-[background-color,scale] duration-150 ease-out hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50'
  return (
    <div className={cx('flex', className)}>
      <button
        type="button"
        disabled={disabled || loading}
        onClick={onClick}
        className={cx(
          fill,
          'min-h-12 flex-1 gap-2 rounded-l-xl px-5 py-3 text-base enabled:active:scale-[0.98]',
        )}
      >
        {loading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : icon}
        {label}
      </button>
      <Menu
        label={menuLabel}
        title={menuLabel}
        side="above"
        width={menuWidth}
        className="flex"
        triggerClassName={cx(
          fill,
          'min-h-12 w-12 rounded-r-xl border-l border-on-fill/35 aria-expanded:bg-primary-dark',
          (disabled || loading) && 'pointer-events-none opacity-50',
        )}
        trigger={<ChevronDown className="size-4" aria-hidden />}
      >
        {children}
      </Menu>
    </div>
  )
}
